import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import { Box, Button, Grid2, Typography } from "@mui/material";
import { FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { FreelancerProfileDocument } from "../../../../../models/FreelancerProfileDocument";
import { FreelancerProfileUrl } from "../../../../../models/FreelancerProfileUrl";
import { zoullooColors } from "../../../../../theme";
import { FreelancerOnboardingMyProfessionalInformationStepViewModel } from "../../../../../ViewModels/FreelancerOnboardingMyProfessionalInformationStepViewModel";
import DocumentTable from "../../../../common/DocumentTable";
import UrlTable from "../common/UrlTable";

interface DocumentsAndUrlsSectionProps {
	formik: FormikProps<FreelancerOnboardingMyProfessionalInformationStepViewModel>;
	activeTable: "documents" | "urls" | null;
	setActiveTable: (table: "documents" | "urls" | null) => void;
	isAddingDocument: boolean;
	isAddingUrl: boolean;
	addingDocument: FreelancerProfileDocument | null;
	addingUrl: FreelancerProfileUrl | null;
	setAddingDocument: React.Dispatch<
		React.SetStateAction<FreelancerProfileDocument | null>
	>;
	setAddingUrl: React.Dispatch<React.SetStateAction<FreelancerProfileUrl | null>>;
	documentTypes: Array<{ id: number; label: string }>;
	urlTypes: Array<{ id: number; label: string }>;
	handleAddNewDocument: () => void;
	handleAddNewUrl: () => void;
	handleFileUpload: (file: File) => Promise<void>;
	handleConfirmAddDocument: () => void;
	handleCancelAddDocument: () => void;
	handleConfirmAddUrl: () => void;
	handleCancelAddUrl: () => void;
	removeDocument: (index: number) => void;
	removeUrl: (index: number) => void;
}

const DocumentsAndUrlsSection: React.FC<DocumentsAndUrlsSectionProps> = ({
	formik,
	activeTable,
	setActiveTable,
	isAddingDocument,
	isAddingUrl,
	addingDocument,
	addingUrl,
	setAddingDocument,
	setAddingUrl,
	documentTypes,
	urlTypes,
	handleAddNewDocument,
	handleAddNewUrl,
	handleFileUpload,
	handleConfirmAddDocument,
	handleCancelAddDocument,
	handleConfirmAddUrl,
	handleCancelAddUrl,
	removeDocument,
	removeUrl
}) => {
	const { t } = useTranslation();

	return (
		<Box sx={{ mb: 4 }}>
			<Typography variant="h4" sx={{ mb: 2 }}>
				{t("freelancerOnboardingMyProfessionalInformation.documentsAndLinks.title")}
			</Typography>
			<Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
				{t("freelancerOnboardingMyProfessionalInformation.documentsAndLinks.description")}
			</Typography>

			{/* Toggle buttons for Documents and URLs */}
			<Grid2 container spacing={2} sx={{ mb: 2 }}>
				<Grid2 size={{ xs: 12, md: 3 }}>
					<Button
						data-testid="toggle-url-table-button"
						startIcon={<LinkIcon />}
						onClick={() => setActiveTable(activeTable === "urls" ? null : "urls")}
						variant="outlined"
						size="large"
						disableRipple
						fullWidth
						sx={{
							color: zoullooColors.grey[700],
							borderColor:
								activeTable === "urls"
									? zoullooColors.secondary?.main || "#4caf50"
									: zoullooColors.grey[400],
							borderWidth: activeTable === "urls" ? 2 : 1,
							"&:hover": {
								borderColor:
									activeTable === "urls"
										? zoullooColors.secondary?.main || "#4caf50"
										: zoullooColors.grey[700],
								backgroundColor: zoullooColors.grey[100]
							}
						}}
					>
						{t("freelancerOnboardingMyProfessionalInformation.documentsAndLinks.addUrl")}
					</Button>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 3 }}>
					<Button
						data-testid="toggle-document-table-button"
						startIcon={<CloudUploadIcon />}
						onClick={() =>
							setActiveTable(activeTable === "documents" ? null : "documents")
						}
						variant="outlined"
						size="large"
						disableRipple
						fullWidth
						sx={{
							color: zoullooColors.grey[700],
							borderColor:
								activeTable === "documents"
									? zoullooColors.secondary?.main || "#4caf50"
									: zoullooColors.grey[400],
							borderWidth: activeTable === "documents" ? 2 : 1,
							"&:hover": {
								borderColor:
									activeTable === "documents"
										? zoullooColors.secondary?.main || "#4caf50"
										: zoullooColors.grey[700],
								backgroundColor: zoullooColors.grey[100]
							}
						}}
					>
						{t(
							"freelancerOnboardingMyProfessionalInformation.documentsAndLinks.addDocument"
						)}
					</Button>
				</Grid2>
			</Grid2>

			{/* Documents Table */}
			<DocumentTable
				documents={formik.values.documents || []}
				isVisible={activeTable === "documents"}
				isAddingDocument={isAddingDocument}
				addingDocument={addingDocument}
				onAddNewDocument={handleAddNewDocument}
				onFileUpload={handleFileUpload}
				onConfirmAddDocument={handleConfirmAddDocument}
				onCancelAddDocument={handleCancelAddDocument}
				onRemoveDocument={removeDocument}
				onDocumentChange={doc => setAddingDocument(prev => ({ ...prev!, ...doc }))}
				showTypeColumn={true}
				documentTypes={documentTypes}
				tableWidth="80%"
				useExperiencesContainer={false}
			/>

			{/* URLs Table */}
			<UrlTable
				urls={formik.values.urls || []}
				isVisible={activeTable === "urls"}
				isAddingUrl={isAddingUrl}
				addingUrl={addingUrl}
				onAddNewUrl={handleAddNewUrl}
				onConfirmAddUrl={handleConfirmAddUrl}
				onCancelAddUrl={handleCancelAddUrl}
				onRemoveUrl={removeUrl}
				onUrlChange={url => setAddingUrl(prev => ({ ...prev!, ...url }))}
				showTypeColumn={true}
				urlTypes={urlTypes}
				tableWidth="80%"
			/>
		</Box>
	);
};

export default DocumentsAndUrlsSection;
